import SettingConfig from '@module/config/setting';
import { xDeviceInfoLocalStorage } from '../../lib/storage/storageImplementations';
import { checkIsMobile } from '../checkIsMobile';
import { BaseHttpResponseError } from '../error/apiErrors';
import parseJsonSafely from '../parseJsonSafely';
import getUserAgentInfo from '../user-agent/getUserAgentInfo';
import { customReturnFetch } from './create-custom-fetch';

const serializeHeaders = (headers: Headers): Record<string, string> => {
  const serialized: Record<string, string> = {};
  headers.forEach((value, key) => {
    serialized[key] = value;
  });
  return serialized;
};

// 에러 컨텍스트/로깅에 실리는 요청 body에서 세션·인증 값은 가린다(민감정보 유출 방지).
// 실제 전송 body(init.body)는 건드리지 않으며, 여기서 만든 사본만 로그/Sentry로 흘러간다.
const SENSITIVE_BODY_KEYS = new Set(['token', 'chatToken', 'sessKey', 'pw', 'newPw', 'password']);

const redactSensitive = (obj: Record<string, any>): Record<string, any> => {
  const redacted: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    redacted[key] = SENSITIVE_BODY_KEYS.has(key) ? '[redacted]' : value;
  }
  return redacted;
};

const serializeRequestBody = (body: BodyInit | null): any => {
  if (!body) return null;

  // 앱 API layer는 convertFormData로 URLSearchParams를 만든다(FormData 아님). 둘 다 key/value로 평탄화.
  if (body instanceof FormData || body instanceof URLSearchParams) {
    const serialized: Record<string, any> = {};
    (body as URLSearchParams).forEach((value: unknown, key: string) => {
      serialized[key] = value;
    });
    return redactSensitive(serialized);
  }

  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      return parsed && typeof parsed === 'object' ? redactSensitive(parsed) : parsed;
    } catch {
      return body;
    }
  }

  return String(body);
};

const fetchExtendedBase = customReturnFetch({
  baseUrl: SettingConfig.API_URL,
  interceptors: {
    response: async (response, [url, init]) => {
      try {
        if (!response.ok) {
          const serializedHeaders = init?.headers ? serializeHeaders(new Headers(init.headers)) : undefined;
          const serializedBody = init?.body ? serializeRequestBody(init.body) : undefined;
          const httpResponse = parseJsonSafely(await response.text());
          throw new BaseHttpResponseError('FetchExtended response was not ok.', httpResponse, {
            request: {
              url,
              method: init?.method ?? 'GET',
              headers: serializedHeaders,
              body: serializedBody,
            },
          });
        }
        const cloneResponse = response.clone();

        const data = await cloneResponse.json();
        if ('loginInfo' in data) {
          const userLoginIdx = data.loginInfo?.userInfo?.idx || 0;
          const prev = xDeviceInfoLocalStorage.get();
          if (prev && prev.ui !== String(userLoginIdx)) {
            prev.ui = String(userLoginIdx);
            xDeviceInfoLocalStorage.set(prev);
          }
        }
        return response;
      } catch (error) {
        if (error instanceof BaseHttpResponseError) {
          console.error(error.serverResponse.errorData?.code, error);
        } else {
          console.error(error);
        }
        throw error;
      }
    },
    request: async (args) => {
      const [input, init] = args;
      const headers = new Headers(init?.headers);

      const { isChatApp, chatAppVersion, isWebView } = getUserAgentInfo();

      const deviceInfo = xDeviceInfoLocalStorage.get();
      if (deviceInfo) {
        deviceInfo.t = checkIsMobile() ? 'webMobile' : 'webPc';
        if (isChatApp) {
          deviceInfo.t = 'chatApp';
          deviceInfo.v = chatAppVersion;
        } else if (isWebView) {
          // temporarily disabled due to issues with webview
          // deviceInfo.t = 'android';
        }
        headers.set('X-Device-Info', JSON.stringify(deviceInfo));
      }

      if (SettingConfig.isFanza) {
        headers.set('Accept-Language', 'ja');
      }

      return [input, { ...init, headers }];
    },
  },
});

export const fetchExtended = async (input: RequestInfo | URL, init?: RequestInit) => {
  try {
    return await fetchExtendedBase(input, init);
  } catch (error) {
    // response interceptor를 거치지 않고 reject된 에러 (fetch API 자체 에러)
    // BaseHttpResponseError가 아닌 경우에만 로깅 (response interceptor에서 이미 로깅됨)
    if (!(error instanceof BaseHttpResponseError)) {
      const serializedHeaders = init?.headers ? serializeHeaders(new Headers(init.headers)) : undefined;
      const serializedBody = init?.body ? serializeRequestBody(init.body) : undefined;
      console.error('[FetchExtended Request Error]', {
        url: input,
        method: init?.method || 'GET',
        headers: serializedHeaders,
        body: serializedBody,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
    throw error;
  }
};
